import { staffAuthHeaders } from '@/lib/api/staffAuthHeaders'
/**
 * Email Test Service
 * Handles API calls for the admin testing dashboard - email testing
 */

const API_BASE = import.meta.env.VITE_API_URL || ''

export interface EmailType {
  label: string
  description: string
  params: string[]
}

export interface EmailTypes {
  [key: string]: EmailType
}

export interface StageAction {
  id: string
  label: string
  description: string
  emails: string[]
  emailDetails: Array<{
    type: string
    label: string
    description: string
    params: string[]
  }>
}

class EmailTestService {
  /**
   * Get all available email types with their parameters
   */
  async getEmailTypes(): Promise<EmailTypes> {
    try {
      const response = await fetch(`${API_BASE}/api/testing/email/types`, { headers: await staffAuthHeaders() })
      const result = await response.json()

      if (!result.success) {
        throw new Error(result.message || 'Failed to fetch email types')
      }

      return result.data || {}
    } catch (error) {
      console.error('[EmailTestService] Error fetching email types:', error)
      return {}
    }
  }

  /**
   * Send a test email
   */
  async sendTestEmail(
    emailType: string,
    adminEmail: string,
    params: Record<string, any>
  ): Promise<{ success: boolean; message: string }> {
    try {
      const response = await fetch(`${API_BASE}/api/testing/email/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(await staffAuthHeaders())
        },
        body: JSON.stringify({
          emailType,
          adminEmail,
          params
        })
      })

      const result = await response.json()

      if (!result.success) {
        throw new Error(result.message || 'Failed to send test email')
      }

      return {
        success: true,
        message: result.message || 'Email sent successfully'
      }
    } catch (error) {
      console.error('[EmailTestService] Error sending test email:', error)
      throw error
    }
  }

  /**
   * Get stage definitions with their actions
   */
  async getSimulationStages(): Promise<StageAction[]> {
    try {
      const response = await fetch(`${API_BASE}/api/testing/simulation/stages`, { headers: await staffAuthHeaders() })
      const result = await response.json()

      if (!result.success) {
        throw new Error(result.message || 'Failed to fetch simulation stages')
      }

      return result.data || []
    } catch (error) {
      console.error('[EmailTestService] Error fetching simulation stages:', error)
      return []
    }
  }

  /**
   * Trigger a simulation action
   */
  async triggerSimulationAction(
    action: string,
    bookingId: string | null,
    adminEmail: string,
    params: Record<string, any>
  ): Promise<{ success: boolean; message: string }> {
    try {
      const response = await fetch(`${API_BASE}/api/testing/simulation/trigger`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(await staffAuthHeaders())
        },
        body: JSON.stringify({
          action,
          bookingId,
          adminEmail,
          params
        })
      })

      const result = await response.json()

      if (!result.success) {
        throw new Error(result.message || 'Failed to trigger action')
      }

      return {
        success: true,
        message: result.message || 'Action triggered successfully'
      }
    } catch (error) {
      console.error('[EmailTestService] Error triggering simulation action:', error)
      throw error
    }
  }
}

export const emailTestService = new EmailTestService()
